import {
  AllegroCreateResponsiblePersonRequest,
  AllegroResponsiblePerson,
  AllegroResponsiblePersonAddress,
  AllegroResponsiblePersonContact,
  AllegroResponsiblePersonPersonalData,
  AllegroUpdateResponsiblePersonRequest,
} from '../../../api/allegro/responsibleperson/responsiblePersonTypes';
import type { ExcelRow } from '../../excel/types';
import {
  createAllegroResponsiblePerson,
  updateAllegroResponsiblePerson,
} from '../../../api/allegro/responsibleperson/responsiblePerson';
import parseExcel from '../../excel/parseExcel';
import COLUMNS from './responsiblePersonExcelSchema';
import { showErrorMessage, showSuccessMessage } from '../../../utils/message';
import {
  extractErrorPayload, isAllegroUnauthorized, readString, runTasksSequentially,
} from '../utils';

type UploadTask = () => Promise<AllegroResponsiblePerson>;

function mapAddress(row: ExcelRow): AllegroResponsiblePersonAddress {
  return {
    countryCode: readString(row, COLUMNS.countryCode),
    street: readString(row, COLUMNS.street),
    postalCode: readString(row, COLUMNS.postalCode),
    city: readString(row, COLUMNS.city),
  } as AllegroResponsiblePersonAddress;
}

function mapContact(row: ExcelRow): AllegroResponsiblePersonContact {
  return {
    email: readString(row, COLUMNS.email),
    phoneNumber: readString(row, COLUMNS.phoneNumber),
    formUrl: readString(row, COLUMNS.formUrl),
  };
}

function mapPersonalData(row: ExcelRow): AllegroResponsiblePersonPersonalData {
  return {
    name: readString(row, COLUMNS.name),
    address: mapAddress(row),
    contact: mapContact(row),
  } as AllegroResponsiblePersonPersonalData;
}

function mapCreateRequest(row: ExcelRow): AllegroCreateResponsiblePersonRequest {
  return {
    name: readString(row, COLUMNS.label),
    personalData: mapPersonalData(row),
  } as AllegroCreateResponsiblePersonRequest;
}

function mapUpdateRequest(id: string, row: ExcelRow): AllegroUpdateResponsiblePersonRequest {
  return {
    id,
    ...mapCreateRequest(row),
  };
}

function prepareUploadTasks(marketplaceCode: string, rows: ExcelRow[]): UploadTask[] {
  return rows.map((row) => {
    const id = readString(row, COLUMNS.id);

    if (id) {
      return () => updateAllegroResponsiblePerson(marketplaceCode, id, mapUpdateRequest(id, row));
    }

    return () => createAllegroResponsiblePerson(marketplaceCode, mapCreateRequest(row));
  });
}

export default async function uploadResponsiblePersonsExcel(
  marketplaceCode: string,
  file: File,
): Promise<void> {
  const rows = await parseExcel(file);
  const tasks = prepareUploadTasks(marketplaceCode, rows);
  const errors: any[] = [];

  await runTasksSequentially(
    tasks,
    (reason) => {
      if (isAllegroUnauthorized(reason)) {
        return;
      }

      errors.push(extractErrorPayload(reason));
    },
  );

  if (errors.length === 0) {
    showSuccessMessage('Responsible persons successfully uploaded.');
  } else {
    showErrorMessage(JSON.stringify(errors));
  }
}
